import type { CraftableId } from './craftables';

// WHERE each deployable may be placed. Surfaces: 'ground' = bare terrain, 'floor' = a built foundation/ceiling TOP
// (face normal up), 'wall' = a built vertical face, 'ceiling' = a built UNDERSIDE (face normal down).
// outsideOnly = must not be under a roof. minSpacing = metres a same-id deployable must keep from another
// (mines/spikes can't be packed). mount = how a wall/ceiling placement seats + orients (see MountSpec).
// attach = it doesn't free-place; it snaps to a target ('lock' -> door/chest/baseclaim, 'window' -> a wall
// window socket) handled by a dedicated flow.
export type Surf = 'ground' | 'floor' | 'wall' | 'ceiling';

// How a surface-mounted deployable seats against the host face. anchor: which of the item's own
// half-extents stands it off the surface ('back' = its half-DEPTH off a wall, 'top' = its half-HEIGHT
// hung below a ceiling). orient: 'faceNormal' = yaw so its front points out of the wall; 'upright' =
// keep the player's chosen yaw. The standoff is derived from the item's OWN footprint (deployFoot), so
// the clip test never samples inside the host -- the old fixed 0.12m standoff was thinner than most
// items and made every wall mount read as Blocked.
export interface MountSpec {
  anchor: 'back' | 'top';
  orient: 'faceNormal' | 'upright';
}

export interface PlaceRule {
  surfaces: Surf[];
  outsideOnly?: boolean;
  minSpacing?: number;
  mount?: MountSpec;
  attach?: 'lock' | 'window' | 'doorway';
}

const RULES: Partial<Record<CraftableId, PlaceRule>> = {
  baseclaim: { surfaces: ['floor'] },                                  // foundations/ceilings only (never bare ground)
  bed: { surfaces: ['floor'] },                                        // beds only on a built floor
  sleeping_bag: { surfaces: ['ground', 'floor'] },                     // bags anywhere on a floor/ground
  spikes: { surfaces: ['ground'], outsideOnly: true, minSpacing: 2.5 },
  mine: { surfaces: ['ground'], outsideOnly: true, minSpacing: 3.2 },
  flame_trap: { surfaces: ['ground'] },                               // flame turret: ground only
  pistol_trap: { surfaces: ['ground', 'floor', 'wall'], mount: { anchor: 'back', orient: 'faceNormal' } },
  shotgun_trap: { surfaces: ['ground', 'floor', 'wall'], mount: { anchor: 'back', orient: 'faceNormal' } },
  water_catcher: { surfaces: ['ground', 'floor'], outsideOnly: true },
  large_water_catcher: { surfaces: ['ground', 'floor'], outsideOnly: true },
  wooden_lock: { surfaces: [], attach: 'lock' },                      // attach to a door/chest/baseclaim, never free-place
  keypad_lock: { surfaces: [], attach: 'lock' },
  wood_window: { surfaces: [], attach: 'window' },                    // snap to a wall window socket
  metal_window: { surfaces: [], attach: 'window' },
  glass_window: { surfaces: [], attach: 'window' },
  reinforced_window: { surfaces: [], attach: 'window' },
  wooden_door: { surfaces: [], attach: 'doorway' },                   // crafted doors snap into a doorway socket
  metal_door: { surfaces: [], attach: 'doorway' },
  metal_double_door: { surfaces: [], attach: 'doorway' },             // into a DOUBLE doorway
  reinforced_door: { surfaces: [], attach: 'doorway' },               // single leaf, places like a metal door
  // Wall/ceiling furnishings: back-anchored wall mounts face out of the wall; the lantern hangs
  // upright below a ceiling underside.
  torch_sconce: { surfaces: ['wall'], mount: { anchor: 'back', orient: 'faceNormal' } },
  shelf: { surfaces: ['wall'], mount: { anchor: 'back', orient: 'faceNormal' } },
  wood_sign: { surfaces: ['wall'], mount: { anchor: 'back', orient: 'faceNormal' } },
  hanging_lantern: { surfaces: ['ceiling'], mount: { anchor: 'top', orient: 'upright' } },
};
// Benches/furnaces/research/repair/refinery/fridge/locker/drop_box: a built floor or the ground, free rotation.
const DEFAULT_RULE: PlaceRule = { surfaces: ['ground', 'floor'] };

export function placeRule(id: CraftableId): PlaceRule { return RULES[id] ?? DEFAULT_RULE; }
